// WaterfallBridge — a net-income bridge (prior → revenue Δ → cost Δ → expense Δ
// → latest). Inline SVG, no chart libraries. Anchored start/end bars are grey;
// step bars are teal (favorable) / clay (unfavorable) so the contribution of
// each driver reads at a glance. Colorblind-safe (hue + position, signed labels).
//
// When `interactive`, hovering a bar reveals its value and clicking a bar
// resolves to that element (`onSelect({kind:'element', ...})`) so the analysis
// can begin on that specific driver; clicking the chart background resolves to
// `{kind:'chart'}` (the bridge in general).

import { useState } from 'react'
import { CfoChartFrame, useChartTooltip, type CfoChartSelection } from './chartInteraction'

export type WaterfallStep = { label: string; delta: number }

export type WaterfallBridgeProps = {
  start: { label: string; value: number }
  steps: WaterfallStep[]
  end: { label: string; value: number }
  ariaLabel: string
  formatValue?: (value: number) => string
  /** Enable hover tooltips + clickable bars. */
  interactive?: boolean
  /** Resolve a clicked bar to an element, or the background to the chart. */
  onSelect?: (selection: CfoChartSelection) => void
  width?: number
  height?: number
  className?: string
}

export type WaterfallBar = {
  label: string
  /** Bar bottom value (running total before this step, or 0 for anchors). */
  base: number
  /** Bar top value. */
  top: number
  kind: 'anchor' | 'pos' | 'neg'
  /** Signed delta for step bars, or the absolute value for anchors. */
  display: number
}

/** Build the bar list (anchors + signed steps) with running totals. Testable. */
export function computeWaterfallBars(
  start: { label: string; value: number },
  steps: WaterfallStep[],
  end: { label: string; value: number },
): WaterfallBar[] {
  const bars: WaterfallBar[] = []
  bars.push({ label: start.label, base: 0, top: start.value, kind: 'anchor', display: start.value })
  let running = start.value
  for (const step of steps) {
    const next = running + step.delta
    bars.push({
      label: step.label,
      base: Math.min(running, next),
      top: Math.max(running, next),
      kind: step.delta >= 0 ? 'pos' : 'neg',
      display: step.delta,
    })
    running = next
  }
  bars.push({ label: end.label, base: 0, top: end.value, kind: 'anchor', display: end.value })
  return bars
}

export function WaterfallBridge({
  start,
  steps,
  end,
  ariaLabel,
  formatValue,
  interactive = false,
  onSelect,
  width = 920,
  height = 260,
  className,
}: WaterfallBridgeProps) {
  const { containerRef, tip, show, hide } = useChartTooltip()
  const [hover, setHover] = useState<number | null>(null)
  const bars = computeWaterfallBars(start, steps, end)
  const padTop = 24
  const padBottom = 48
  const padX = 24
  const plotH = height - padTop - padBottom
  const allVals = bars.flatMap((b) => [b.base, b.top, 0])
  const min = Math.min(...allVals)
  const max = Math.max(...allVals)
  const span = max - min || 1
  const toY = (v: number) => padTop + (1 - (v - min) / span) * plotH
  const zeroY = toY(0)
  const slot = (width - padX * 2) / bars.length
  const barW = Math.min(86, slot * 0.62)

  const color = (kind: WaterfallBar['kind']) =>
    kind === 'anchor' ? 'var(--anchor, #9aa0a8)' : kind === 'pos' ? 'var(--pos, #0e7c86)' : 'var(--neg, #c0492f)'
  const fmt = (v: number) => (formatValue ? formatValue(v) : String(Math.round(v)))
  const tipValue = (bar: WaterfallBar) =>
    bar.kind === 'anchor' ? fmt(bar.display) : `${bar.display >= 0 ? '+' : '\u2212'}${fmt(Math.abs(bar.display))}`

  // Labels are centred under bars of width `slot`, so past roughly eight bars
  // full-length text from adjacent bars overlaps and both become unreadable.
  // Truncating to what the slot can hold keeps every label legible; the full
  // text stays reachable on hover and in the accessible name, so nothing is
  // lost -- which is the reason `interactive` had to be switched on with this.
  const CHAR_PX = 6.1 // 11px semibold Inter, measured average advance
  const maxChars = Math.max(3, Math.floor((slot - 6) / CHAR_PX))
  const clip = (label: string) =>
    label.length <= maxChars ? label : `${label.slice(0, Math.max(1, maxChars - 1))}\u2026`

  const svg = (
    <svg className={className} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
      <line x1={padX} y1={zeroY} x2={width - padX} y2={zeroY} stroke="var(--axis, #3a3f47)" strokeWidth={1.2} />
      {bars.map((bar, i) => {
        const cx = padX + slot * i + slot / 2
        const x = cx - barW / 2
        const y = toY(bar.top)
        const h = Math.max(2, toY(bar.base) - toY(bar.top))
        const hovered = hover === i
        return (
          <g key={`${bar.label}-${i}`}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              rx={2}
              fill={color(bar.kind)}
              opacity={bar.kind === 'anchor' ? (hovered ? 0.95 : 0.85) : hovered ? 0.85 : 1}
              stroke={hovered ? 'var(--ink, #16242c)' : 'none'}
              strokeWidth={hovered ? 1.4 : 0}
            />
            {formatValue ? (
              <text
                x={cx}
                y={y - 6}
                textAnchor="middle"
                style={{ font: '700 11px var(--font-ui)', fill: 'var(--ink-2, #4c5360)' }}
              >
                {tipValue(bar)}
              </text>
            ) : null}
            <text
              x={cx}
              y={height - 26}
              textAnchor="middle"
              style={{ font: '600 11px var(--font-ui)', fill: 'var(--muted, #868c9a)' }}
            >
              {clip(bar.label)}
              {/* The untruncated label, for hover and for a screen reader. */}
              <title>{bar.label}</title>
            </text>
            {interactive ? (
              <rect
                className="cfo-chart-bar-hit"
                x={padX + slot * i}
                y={padTop}
                width={slot}
                height={plotH}
                fill="transparent"
                role={onSelect ? 'button' : undefined}
                tabIndex={onSelect ? 0 : undefined}
                aria-label={`Analyze ${bar.label}: ${tipValue(bar)}`}
                onMouseEnter={(e) => {
                  setHover(i)
                  show(e.clientX, e.clientY, tipValue(bar), bar.label)
                }}
                onMouseMove={(e) => show(e.clientX, e.clientY, tipValue(bar), bar.label)}
                onMouseLeave={() => {
                  setHover(null)
                  hide()
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  onSelect?.({ kind: 'element', label: bar.label, value: bar.display, index: i })
                }}
                onKeyDown={(e) => {
                  if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    onSelect({ kind: 'element', label: bar.label, value: bar.display, index: i })
                  }
                }}
              />
            ) : null}
          </g>
        )
      })}
    </svg>
  )
  if (!interactive) return svg
  return (
    <CfoChartFrame containerRef={containerRef} tip={tip} className={className ? `${className}-frame` : undefined}>
      <div
        onClick={onSelect ? () => onSelect({ kind: 'chart' }) : undefined}
        onMouseLeave={() => {
          setHover(null)
          hide()
        }}
      >
        {svg}
      </div>
    </CfoChartFrame>
  )
}

export default WaterfallBridge
